<template>
    <template v-if="!switcher"></template>
    <BaseDialog
        v-else-if="presentation === 'modal'"
        width="narrow"
        :title="t('navigation.instanceSwitcher.title')"
        :description="t('navigation.instanceSwitcher.description')"
        :show-close="true">
        <template #trigger>
            <button
                type="button"
                data-testid="valencer-instance-switcher-trigger"
                :aria-label="triggerLabel"
                :class="['flex items-center gap-4 px-3 py-4 rounded-xl hover:bg-nav-item-hover-bg transition-colors w-full text-left', triggerClass]">
                <i class="bi bi-globe text-lg leading-none shrink-0 text-nav-icon" aria-hidden="true"></i>
                <template v-if="showLabels">
                    <span class="body text-ink">{{ currentLabel }}</span>
                    <i class="bi bi-chevron-right text-sm ml-auto text-ink-muted" aria-hidden="true"></i>
                </template>
            </button>
        </template>
        <div class="flex flex-col gap-4">
            <div>
                <I18nT keypath="navigation.instanceSwitcher.currentlyOn" tag="p" class="body-small text-ink-muted mt-1">
                    <template #instance>
                        <strong>{{ currentLabel }}</strong>
                    </template>
                </I18nT>
            </div>
            <div class="flex flex-col gap-1">
                <template v-for="opt in switcher.options" :key="opt.key">
                    <a
                        v-if="!opt.is_current"
                        :href="opt.url"
                        :data-testid="`instance-switch-${opt.key}`"
                        class="body text-nav-menu-text hover:bg-nav-menu-hover-bg rounded-lg flex flex-row items-center gap-3 px-3 py-3 leading-none border border-nav-menu-item-border">
                        <i class="bi bi-globe text-nav-menu-icon text-lg" aria-hidden="true"></i>
                        <span class="flex flex-col">
                            <span>{{ opt.label }}</span>
                            <span class="body-small text-ink-muted">{{ opt.host }}</span>
                        </span>
                        <i class="bi bi-arrow-right text-ink-muted ml-auto" aria-hidden="true"></i>
                    </a>
                </template>
            </div>
        </div>
    </BaseDialog>
    <BaseDropdownMenu v-else :sections="instanceMenuSections" side="right" align="start" :label="t('navigation.instanceSwitcher.title')" content-class="min-w-52">
        <template #trigger>
            <BaseTooltip side="right" :disabled="showLabels">
                <template #trigger>
                    <button type="button" data-testid="valencer-instance-switcher-trigger" :aria-label="triggerLabel" :class="['flex items-center w-full text-left', triggerClass]">
                        <i class="bi bi-globe leading-none shrink-0" :class="legacy ? 'nav-icon-legacy' : 'nav-icon'" aria-hidden="true"></i>
                        <template v-if="showLabels">
                            <span class="min-w-0 truncate" :class="{ 'ml-3': legacy }">{{ currentLabel }}</span>
                            <i class="bi bi-chevron-right text-sm ml-auto text-ink-muted" aria-hidden="true"></i>
                        </template>
                    </button>
                </template>
                <template #content>{{ triggerLabel }}</template>
            </BaseTooltip>
        </template>
        <template #item-current-instance="{ item }">
            <i class="bi bi-check2 shrink-0 text-base leading-none" aria-hidden="true"></i>
            {{ item.label }} <span class="body-small">{{ t("navigation.instanceSwitcher.current") }}</span>
        </template>
    </BaseDropdownMenu>
</template>

<script setup lang="ts">
import type { UserDetails } from "~vue/appTypes";
import BaseDialog from "~vue/components/BaseDialog.vue";
import BaseDropdownMenu, { type DropdownMenuItem, type DropdownMenuSection } from "~vue/components/BaseDropdownMenu.vue";
import BaseTooltip from "~vue/components/BaseTooltip.vue";
import { computed, type ComputedRef, inject } from "vue";
import { I18nT, useI18n } from "vue-i18n";

const { t } = useI18n();

withDefaults(
    defineProps<{
        // Class applied to the trigger button. Callers pass their nav-item class
        // so the trigger visually blends into whatever menu it's rendered inside.
        triggerClass?: string;
        // Expanded (label + chevron visible) or collapsed (icon only, tooltip label).
        showLabels?: boolean;
        // "popover" (default) for desktop nav — small dropdown next to the trigger.
        // "modal" for mobile nav — full bottom-sheet dialog opened over the whole app.
        presentation?: "popover" | "modal";
        // Flag-off sidebar: legacy icon size, and a label margin since legacy rows have no gap.
        legacy?: boolean;
    }>(),
    {
        showLabels: true,
        presentation: "popover",
        legacy: false,
    },
);

const userDetails = inject<ComputedRef<UserDetails> | undefined>("user_details");
const switcher = computed(() => userDetails?.value?.valencer_instance_switcher ?? null);
const instanceMenuSections = computed<DropdownMenuSection[]>(() => [
    {
        id: "instances",
        items: (switcher.value?.options ?? []).map((option): DropdownMenuItem => {
            if (option.is_current) {
                return {
                    id: "current-instance",
                    type: "button",
                    label: option.label,
                    disabled: true,
                    ariaCurrent: true,
                };
            }
            return {
                id: `instance-${option.key}`,
                type: "anchor",
                href: option.url,
                label: option.label,
                icon: "bi bi-globe",
                testId: `instance-switch-${option.key}`,
            };
        }),
    },
]);

const currentLabel = computed(() => {
    const current = switcher.value?.options.find((o) => o.is_current);
    return current?.label ?? t("navigation.instanceSwitcher.instance");
});

const triggerLabel = computed(() => t("navigation.instanceSwitcher.trigger", { instance: currentLabel.value }));
</script>
